import React, {Component} from 'react';
import PropTypes from 'prop-types';
import {withRouter} from 'react-router-dom';
import {connect} from 'react-redux';
import {getSchools, resetSchoolsSearchCriteria, setUiSchoolFocused, rememberScrollPositionListing, rememberPaginationListing} from '../../actions';
import SchoolsListItem from './SchoolsListItem';
import SchoolsListItemPlaceholder from './SchoolsListItemPlaceholder';
import LoadingSpinner from '../../../Layout/components/LoadingSpinner';
import Button from '@material-ui/core/Button';
import {withLocalize, Translate} from 'react-localize-redux';
import translations from '../../translations/translations_schools_list';
import Waypoint from 'react-waypoint';
import classnames from 'classnames';
import {withLastLocation} from 'react-router-last-location';
import translationsRoutes from '../../../../app/router/translations_routes';

const NUMBER_ITEMS_PER_LINE = 3;
const NUMBER_ITEMS_PLACEHOLDER_INITIAL_LOADING = 12;

const routes = Object.values(translationsRoutes);

class SchoolsList extends Component {
    state = {
        initialLoad: true,
        hasCameFromOtherPage: false,
    };

    constructor(props) {
        super(props);

        this.props.addTranslation(translations);
    }

    fetchContent() {
        const {search, loading, uiRememberPaginationListing} = this.props;

        if (!loading) {
            if (search.submitted) {
                // The search form was submitted from another page.
                // The Redux store has the search criteria in memory.
                // Fetch the schools with these criteria...

                let page = 1; // No offset
                let pageWithPreviousResults = false;

                if (this.hasCameFromDetailsPage()) {
                    // Restore the pagination
                    page = uiRememberPaginationListing;
                    pageWithPreviousResults = true;
                }

                this.props.getSchools(
                    page,
                    pageWithPreviousResults, // Page with previous results
                    true, // Reset the search results / listing
                    search.type,
                    search.programme,
                    search.degree
                );
            } else {
                // Fetch the schools without any search criteria
                this.props.getSchools(
                    1, // No offset
                    false, // Page with previous results
                    true, // Reset the search results / listing
                );
            }
        }
    }

    componentDidMount() {
        this.fetchContent();
    }

    componentDidUpdate(prevProps, prevState) {
        const {uiRememberScrollPositionListing, loading, schools} = this.props;

        if (prevProps.languages !== this.props.languages) {
            const {pathname} = this.props.location;
            const expectedUri = this.props.translate('routes.schools');

            // The taxonomies ID, for the current active language, are stored in Redux.
            // If we don't reset the search criteria, when the language is switched, the ID matching to the previous language will be used in for the server requests.
            // With this reset, the user will be redirect to the search listing in the selected language, without any search criteria selected.
            this.props.resetSchoolsSearchCriteria();

            // The timeout is not ideal here... we can't assume the time of resetSchoolsSearchCriteria()
            setTimeout(() => {
                if (pathname !== expectedUri) {
                    this.props.history.push(expectedUri);
                } else {
                    this.fetchContent();
                }
            }, 500);
        }

        // Restore the scroll position (if the user came from a school detail page)
        if (this.state.hasCameFromOtherPage === false && this.hasCameFromDetailsPage() && (!loading && Array.isArray(schools) && schools.length > 0) && uiRememberScrollPositionListing > 0) {
            this.setState({
                hasCameFromOtherPage: true,
            }, () => window.scrollTo(0, uiRememberScrollPositionListing));
        }
    }

    componentWillUnmount() {
        this.props.setUiSchoolFocused(0);
        this.props.rememberScrollPositionListing(window.scrollY);
        this.props.rememberPaginationListing(this.props.currentPage);
    }

    hasCameFromDetailsPage = () => {
        const {lastLocation} = this.props;

        if (typeof lastLocation === 'object' && lastLocation !== null && lastLocation.hasOwnProperty('pathname') && lastLocation.pathname.length > 0) {
            if (lastLocation.pathname.startsWith(routes[0].schools[0] + '/' || lastLocation.pathname.startsWith(routes[0].schools[1] + '/'))) {
                return true;
            }
        }

        return false;
    };

    handleScrollLoadMore = () => {
        this.loadMore();
    };

    handleClickLoadMore = (e) => {
        e.preventDefault();

        this.loadMore();
    };

    loadMore() {
        const {loading, hasMore, currentPage, search} = this.props;

        if (hasMore && !loading) {
            this.setState({initialLoad: false});

            this.props.getSchools(
                currentPage + 1,
                false, // Page with previous results
                false, // Reset the search results / listing
                search.type,
                search.programme,
                search.degree
            );
        }
    }

    render() {
        const {schools, loading, hasMore, idSchoolFocused} = this.props;

        let placeholders = [];
        let placeholders2 = [];

        if (this.state.initialLoad) {
            for (let i = 0; i < NUMBER_ITEMS_PLACEHOLDER_INITIAL_LOADING; i++) {
                placeholders.push(<SchoolsListItemPlaceholder key={i}/>);
            }
        } else {
            for (let i = 0; i < NUMBER_ITEMS_PER_LINE; i++) {
                placeholders2.push(<SchoolsListItemPlaceholder key={i}/>);
            }
        }

        return (
            <section id="schools-list" className={classnames({
                'has-school-focused': (idSchoolFocused > 0),
            })}>
                {loading &&
                    <div className="row row-loading-top-mobile d-lg-none">
                        <div className="col-3 mx-auto">
                            <div className="text-center">
                                <LoadingSpinner/>
                            </div>
                        </div>
                    </div>
                }
                <div className={classnames('row', {
                    'searching': loading,
                })}>
                    {(this.state.initialLoad && loading && Array.isArray(schools) && schools.length === 0) && placeholders}
                    {schools.map((school, index) => (
                        <SchoolsListItem key={school.id} index={index} school={school} setUiSchoolFocused={this.props.setUiSchoolFocused} idSchoolFocused={idSchoolFocused} />
                    ))}
                    {(loading && !this.state.initialLoad) && placeholders2}
                    {(!loading && (Array.isArray(schools) && schools.length === 0)) &&
                        <div className="col col-12 text-center">
                            <p><Translate id="schools_list.no_item_found" /></p>
                        </div>
                    }
                </div>
                {(hasMore && !loading) &&
                    <div className="row row-more">
                        <div className="col-6 col-lg-3 mx-auto">
                            <div className="text-center">
                                <Waypoint onEnter={this.handleScrollLoadMore} />
                                <Button id="button-load-more" variant="contained" color="primary" size="medium" onClick={this.handleClickLoadMore}>
                                    <Translate id="schools_list.button_load_more" />
                                </Button>
                            </div>
                        </div>
                    </div>
                }
                {(((Array.isArray(schools) && schools.length !== 0) || loading) && !this.state.initialLoad && hasMore) &&
                    <div className="row row-loading">
                        <div className="col-3 mx-auto">
                            <div className="text-center">
                                <LoadingSpinner/>
                            </div>
                        </div>
                    </div>
                }
            </section>
        );
    }
}

SchoolsList.propTypes = {
    getSchools: PropTypes.func.isRequired,
    resetSchoolsSearchCriteria: PropTypes.func.isRequired,
    setUiSchoolFocused: PropTypes.func.isRequired,
    rememberScrollPositionListing: PropTypes.func.isRequired,
    rememberPaginationListing: PropTypes.func.isRequired,
    translate: PropTypes.func.isRequired,
    uiRememberScrollPositionListing: PropTypes.number,
    uiRememberPaginationListing: PropTypes.number,
};

const mapStateToProps = (state) => ({
    loading: state.schools.schoolsLoading,
    hasMore: state.schools.hasMore,
    currentPage: state.schools.currentPage,
    search: state.schools.search,
    schools: state.schools.schools,
    idSchoolFocused: state.schools.idSchoolFocused,
    uiRememberScrollPositionListing: state.schools.uiRememberScrollPositionListing,
    uiRememberPaginationListing: state.schools.uiRememberPaginationListing,
});

export default withRouter(withLastLocation(connect(mapStateToProps, {
    getSchools,
    resetSchoolsSearchCriteria,
    setUiSchoolFocused,
    rememberScrollPositionListing,
    rememberPaginationListing
})(withLocalize(SchoolsList))));



// WEBPACK FOOTER //
// ./src/features/Schools/components/List/SchoolsList.js